import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { CreditCard, Calendar, Shield, AlertTriangle, CheckCircle, XCircle, RefreshCw, Mail, Zap, User, Save } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export default function ManageSubscription() {
  const { user, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  
  // Profile settings state
  const [profileName, setProfileName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    // Re-fetch subscription status when user changes
    if (user?.id) {
      fetchSubscriptionStatus();
      fetchUserProfile();
    }
  }, [user?.id]);

  const fetchUserProfile = async () => {
    try {
      const res = await api.get('/api/auth/me');
      setProfileName(res.data.name || '');
    } catch (e) {
      console.error('Failed to fetch profile:', e);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch('/api/auth/profile', {
        name: profileName
      });
      toast.success('Profile settings saved!');
      await refreshUser();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const res = await api.get('/api/subscription/status');
      setSubscription(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load subscription status");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      const res = await api.post('/api/subscription/cancel');
      toast.success(res.data.message);
      await fetchSubscriptionStatus();
      await refreshUser();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      const res = await api.post('/api/subscription/reactivate');
      if (res.data.status === 'requires_checkout') {
        // User needs to create new subscription
        toast.info(res.data.message);
        navigate('/upgrade');
        return;
      }
      toast.success(res.data.message);
      await fetchSubscriptionStatus();
      await refreshUser();
    } catch (e) {
      console.error(e);
      // If reactivation fails, redirect to upgrade page
      if (e.response?.status === 400) {
        toast.info("Please create a new subscription");
        navigate('/upgrade');
      } else {
        toast.error(e.response?.data?.detail || "Failed to reactivate subscription");
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await api.post('/api/email/test-reminder');
      toast.success(`Test email sent to ${res.data.email}`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to send test email");
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className={cn(
        "min-h-screen transition-colors duration-300",
        isDark ? "bg-[#050505]" : "bg-[#FAF9F5]"
      )}>
        <Navbar />
        <div className="max-w-2xl mx-auto p-6 flex items-center justify-center h-[60vh]">
          <div className={cn("animate-spin rounded-full h-8 w-8 border-b-2", isDark ? "border-violet-400" : "border-violet-500")}></div>
        </div>
      </div>
    );
  }

  const isPremium = subscription?.is_premium;
  const isCanceling = subscription?.cancel_at_period_end;
  const isGracePeriod = subscription?.is_grace_period;

  return (
    <div className={cn(
      "min-h-screen transition-colors duration-300",
      isDark ? "bg-[#050505]" : "bg-[#FAF9F5]"
    )}>
      <Navbar />
      
      <div className="max-w-2xl mx-auto p-6 md:p-12 space-y-6">
        <div className="space-y-2">
          <h1 className={cn("text-3xl font-bold tracking-tight", isDark ? "text-white" : "text-[#1A1A1A]")}>Manage Subscription</h1>
          <p className={isDark ? "text-zinc-400" : "text-[#9E8778]"}>View and manage your Monk Mode subscription</p>
        </div>

        {/* Current Plan Card - Only show for premium users */}
        {isPremium && (
        <Card className={cn(
          "border transition-colors",
          isDark ? "glass-card card-shine" : "bg-white shadow-lg border-[#EEEAE0]"
        )}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <CardTitle className={cn("text-xl", isDark ? "text-gradient" : "text-[#1A1A1A]")}>{subscription?.plan_name}</CardTitle>
                <CardDescription className={isDark ? "text-zinc-400" : "text-[#9E8778]"}>
                  {isGracePeriod 
                    ? "Your subscription is canceled but you still have access" 
                    : "You have access to all premium features"}
                </CardDescription>
              </div>
              <Badge 
                variant={isCanceling ? "secondary" : "default"} 
                className={`text-sm px-3 py-1 ${isGracePeriod ? 'bg-[#FDF0EC]0/20 text-coral border-[#F5D5CB]' : 'bg-[#F4F3EE]0/20 text-[#5F5F5F] border-violet-500/30'}`}
              >
                {isGracePeriod ? "Ending Soon" : isCanceling ? "Canceling" : "Active"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={cn("flex items-center gap-3 text-sm", isDark ? "text-zinc-300" : "text-[#5F5F5F]")}>
              <CreditCard className={isDark ? "w-5 h-5 text-zinc-500" : "w-5 h-5 text-[#9E8778]"} />
              <span className="font-medium">{subscription?.price}</span>
            </div>
            
            {subscription?.current_period_end && (
              <div className={cn("flex items-center gap-3 text-sm", isGracePeriod ? "text-coral" : (isDark ? "text-zinc-300" : "text-[#5F5F5F]"))}>
                <Calendar className="w-5 h-5 text-zinc-500" />
                <span>
                  {isGracePeriod ? "Premium access ends" : isCanceling ? "Access ends" : "Next billing date"}: {' '}
                  <span className="font-medium">
                    {new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </span>
                </span>
              </div>
            )}

            <div className="flex items-center gap-3 text-sm text-zinc-300">
              <Shield className="w-5 h-5 text-zinc-500" />
              <span>Status: <span className="font-medium capitalize">{isGracePeriod ? "Grace Period" : subscription?.status}</span></span>
            </div>
            
            {isGracePeriod && (
              <div className="mt-4 p-3 rounded-lg bg-[#FDF0EC]0/10 border border-[#F5D5CB]">
                <p className="text-sm text-amber-300">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Your subscription has been canceled. You&apos;ll continue to have premium access until the date shown above. After that, you&apos;ll need to subscribe again with a new payment to regain Pro features.
                </p>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex flex-wrap gap-3">
            {!isCanceling && !isGracePeriod && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="flex-1" disabled={actionLoading}>
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Subscription
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-500" />
                      Cancel Subscription?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="space-y-2">
                      <p>Are you sure you want to cancel your Monk Mode Pro subscription?</p>
                      <ul className="list-disc list-inside text-sm space-y-1 mt-2">
                        <li>You&apos;ll keep premium access until the end of your billing period</li>
                        <li>After that, you&apos;ll lose access to Weekly Tracker, Calendar &amp; Shared Goals</li>
                        <li>Your goals and progress will be preserved</li>
                        <li>To regain Pro features later, you&apos;ll need to make a new payment</li>
                      </ul>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                    <AlertDialogAction onClick={handleCancel} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Yes, Cancel
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            
            {isPremium && (isCanceling || isGracePeriod) && (
              <Button onClick={handleReactivate} disabled={actionLoading} className="flex-1 bg-[#FDF0EC]0 hover:bg-amber-600 text-black">
                <RefreshCw className={`w-4 h-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
                {isGracePeriod ? 'Subscribe Again' : 'Undo Cancellation'}
              </Button>
            )}
          </CardFooter>
        </Card>
        )}

        {/* Email Notifications Card */}
        <Card className={cn(
          "transition-colors",
          !isPremium ? 'opacity-75' : '',
          isDark ? "glass-card card-shine" : "bg-white shadow-sm border-[#EEEAE0]"
        )}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className={cn("text-lg flex items-center gap-2", isDark ? "text-gradient" : "text-[#1A1A1A]")}>
                <Mail className="w-5 h-5" />
                Email Notifications
              </CardTitle>
              {!isPremium && (
                <Badge variant="secondary" className="bg-[#1A1A1A] text-white border-0">
                  Pro Feature
                </Badge>
              )}
            </div>
            <CardDescription className={isDark ? "text-zinc-400" : "text-[#9E8778]"}>
              {isPremium 
                ? "Get daily reminders to stay on track with your goals" 
                : "Upgrade to Pro to unlock email reminders and alerts"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={cn(
              "flex items-start gap-3 p-3 rounded-lg",
              isPremium 
                ? (isDark ? "bg-zinc-800/50" : "bg-white border border-violet-50")
                : (isDark ? "bg-zinc-800/30" : "bg-white/50 border border-violet-50")
            )}>
              <CheckCircle className={cn("w-5 h-5 mt-0.5", isPremium ? "text-green-500" : (isDark ? "text-zinc-500" : "text-[#9E8778]"))} />
              <div>
                <p className={cn("font-medium text-sm", isDark ? "text-zinc-200" : "text-[#5F5F5F]")}>Daily Goal Reminders</p>
                <p className={cn("text-sm", isDark ? "text-zinc-400" : "text-[#9E8778]")}>
                  Receive an email each day with goals that need your attention
                </p>
              </div>
            </div>
            
            <div className={cn(
              "flex items-start gap-3 p-3 rounded-lg",
              isPremium 
                ? (isDark ? "bg-zinc-800/50" : "bg-white border border-violet-50")
                : (isDark ? "bg-zinc-800/30" : "bg-white/50 border border-violet-50")
            )}>
              <CheckCircle className={cn("w-5 h-5 mt-0.5", isPremium ? "text-green-500" : (isDark ? "text-zinc-500" : "text-[#9E8778]"))} />
              <div>
                <p className={cn("font-medium text-sm", isDark ? "text-zinc-200" : "text-[#5F5F5F]")}>Goal Completion Alerts</p>
                <p className={cn("text-sm", isDark ? "text-zinc-400" : "text-[#9E8778]")}>
                  Get notified when you complete or miss a goal
                </p>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            {isPremium ? (
              <Button 
                variant="outline" 
                onClick={handleTestEmail} 
                disabled={testingEmail}
                className={cn(
                  "w-full",
                  isDark ? "border-zinc-700 text-zinc-300 hover:bg-zinc-800" : "border-[#E5E2DA] text-[#5F5F5F] hover:bg-white"
                )}
              >
                <Mail className={`w-4 h-4 mr-2 ${testingEmail ? 'animate-pulse' : ''}`} />
                {testingEmail ? "Sending..." : "Send Test Email"}
              </Button>
            ) : (
              <Button 
                onClick={() => navigate('/upgrade')}
                className="w-full bg-[#1A1A1A] hover:bg-[#2B2B2B]"
              >
                <Zap className="w-4 h-4 mr-2" />
                Upgrade to Unlock
              </Button>
            )}
          </CardFooter>
        </Card>

        {/* Profile Settings Card */}
        <Card className={cn(isDark ? "glass-card card-shine" : "bg-white shadow-sm border-[#EEEAE0]")}>
          <CardHeader>
            <CardTitle className={cn("text-lg flex items-center gap-2", isDark ? "text-gradient" : "text-[#1A1A1A]")}>
              <User className="w-5 h-5" />
              Profile Settings
            </CardTitle>
            <CardDescription className={isDark ? "text-zinc-400" : "text-[#9E8778]"}>
              Manage your account information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="profileName" className={isDark ? "text-zinc-300" : "text-[#5F5F5F]"}>Display Name</Label>
              <Input
                id="profileName"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                placeholder="Enter your name"
                className={cn(
                  isDark ? "bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500" : "bg-white border-[#E5E2DA] text-[#1A1A1A] placeholder:text-[#9E8778]"
                )}
              />
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={handleSaveProfile} 
              disabled={savingProfile}
              className="w-full btn-glossy"
            >
              <Save className={`w-4 h-4 mr-2 ${savingProfile ? 'animate-pulse' : ''}`} />
              {savingProfile ? 'Saving...' : 'Save Profile Settings'}
            </Button>
          </CardFooter>
        </Card>

        {/* Features Comparison */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-lg text-gradient">Features</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { name: "Goal Setting & Tracking", free: true, premium: true },
                { name: "Daily Progress Logs", free: true, premium: true },
                { name: "Basic Stats", free: true, premium: true },
                { name: "Financial Pledges", free: false, premium: true },
                { name: "Weekly Habit Tracker", free: false, premium: true },
                { name: "Performance Calendar", free: false, premium: true },
                { name: "Goal Reminders & Alerts", free: false, premium: true },
                { name: "Extended History", free: false, premium: true },
              ].map((feature, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
                  <span className="text-sm text-zinc-300">{feature.name}</span>
                  <div className="flex gap-8">
                    <span className="w-16 text-center">
                      {feature.free ? (
                        <CheckCircle className="w-5 h-5 text-green-400 inline" />
                      ) : (
                        <XCircle className="w-5 h-5 text-zinc-600 inline" />
                      )}
                    </span>
                    <span className="w-16 text-center">
                      {feature.premium ? (
                        <CheckCircle className="w-5 h-5 text-violet-400 inline" />
                      ) : (
                        <XCircle className="w-5 h-5 text-zinc-600 inline" />
                      )}
                    </span>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 text-sm font-medium text-zinc-500">
                <span></span>
                <div className="flex gap-8">
                  <span className="w-16 text-center">Free</span>
                  <span className="w-16 text-center">Pro</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
